import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../app/hooks';
import { selectUser } from '../../entities/session/model/slice';
import { selectOrganizationId } from '../../entities/organization/model/slice';
import { useGetProjectsQuery } from '../../entities/project/api/projectApi';
import {
    useCreateSupportRequestMutation,
    useGetMySupportRequestsQuery,
} from '../../entities/support/api/supportApi';
import { useGetOrganizationsQuery } from '../../entities/organization/api/organizationApi';
import { Button } from '../../components/ui/Button/Button';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { ContactCard } from '../../components/support/ContactCard/ContactCard';
import { MyTicketsCard } from '../../components/support/MyTicketsCard/MyTicketsCard';
import { SubmitBar } from '../../components/support/SubmitBar/SubmitBar';
import { SuccessModal } from '../../components/support/SuccessModal/SuccessModal';
import { SupportForm } from '../../components/support/SupportForm/SupportForm';
import { TipsCard } from '../../components/support/TipsCard/TipsCard';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { useAttachments } from '../../hooks/useAttachments';
import { useSupportForm } from '../../hooks/useSupportForm';
import type { SelectOption, SubmittedTicket, Ticket } from '../../types/support';
import styles from './SupportPage.module.scss';
import { statusLabels } from '../../entities/support/model/constants';

const MY_TICKETS_LIMIT = 3;

const NO_PROJECT = 'none';

const TOPIC_MAX = 100;

const deriveTopic = (description: string): string => {
    const firstLine = description.trim().split('\n')[0]?.trim() ?? '';

    return firstLine.length > TOPIC_MAX ? `${firstLine.slice(0, TOPIC_MAX - 1).trimEnd()}…` : firstLine;
};

export function SupportPage() {
    const showToast = useToast();
    const { values, errors, setField, validate, reset } = useSupportForm();
    const { files, add, remove, clear } = useAttachments();
    const [submitted, setSubmitted] = useState<SubmittedTicket | null>(null);

    const user = useAppSelector(selectUser);
    const organizationId = useAppSelector(selectOrganizationId);
    const navigate = useNavigate();

    const [createRequest, { isLoading: isSending }] = useCreateSupportRequestMutation();

    const { data: projects = [] } = useGetProjectsQuery(
        { organizationId },
        { skip: !organizationId },
    );

    const { data: myRequests } = useGetMySupportRequestsQuery({
        page_size: MY_TICKETS_LIMIT,
        page_num: 1,
    });

    const { data: organizations = [] } = useGetOrganizationsQuery(
        {},
        { skip: !user?.organization_id },
    );

    const orgName = useMemo(
        () => organizations.find((org) => org.id === user?.organization_id)?.name ?? '',
        [organizations, user?.organization_id],
    );

    const projectOptions: SelectOption[] = useMemo(
        () => [
            { value: NO_PROJECT, label: 'Другое' },
            ...projects.map((project) => ({ value: project.id, label: project.name })),
        ],
        [projects],
    );

    const myTickets: Ticket[] = useMemo(
        () =>
            (myRequests?.items ?? []).map((ticket) => ({
                number: ticket.id,
                title: ticket.problem,
                date: ticket.createdAt,
                status: ticket.status,
                statusLabel: statusLabels[ticket.status],
            })),
        [myRequests],
    );

    const authorName = user?.email ?? '';

    const handleAddFiles = useCallback(
        (incoming: FileList | File[]) => {
            const { added, tooBig, duplicate } = add(incoming);

            if (tooBig) {
                showToast('Файл больше 20 МБ — отправьте ссылку на него в описании', 'error');
            }
            if (duplicate) {
                showToast('Этот файл уже прикреплён');
            }
            if (added) {
                showToast(added === 1 ? 'Файл прикреплён' : 'Файлы прикреплены', 'success');
            }
        },
        [add, showToast],
    );

    const handleRemoveFile = useCallback(
        (id: string) => {
            remove(id);
            showToast('Файл убран из обращения');
        },
        [remove, showToast],
    );

    const resetAll = useCallback(() => {
        reset({ keep: ['email', 'name'] });
        clear();
    }, [reset, clear]);

    const handleSubmit = useCallback(async () => {
        const { errors: found, firstInvalid } = validate();
        const count = Object.keys(found).length;

        if (firstInvalid) {
            const el = document.getElementById(firstInvalid);
            el?.focus();
            el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            showToast(
                count === 1
                    ? 'Один обязательный пункт не заполнен'
                    : `Заполните обязательные пункты — их ${count}`,
                'error',
            );
            return;
        }

        const topic = deriveTopic(values.description);

        try {
            const ticket = await createRequest({
                email: values.email,
                name: values.name,
                organizationId,
                project_id: values.project === NO_PROJECT ? undefined : values.project,
                problem: topic,
                description: values.description,
                comment: '',
                // files пока не отправляем
            }).unwrap();

            setSubmitted({
                number: ticket.id,
                topic: ticket.problem || topic,
            });
        } catch {
            // тост об ошибке уже показал onQueryStarted в supportApi
        }
    }, [validate, values, organizationId, createRequest, showToast]);

    const closeSuccess = useCallback(() => {
        setSubmitted(null);
        resetAll();
    }, [resetAll]);

    const createAnother = useCallback(() => {
        closeSuccess();
        document.getElementById('email')?.focus();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [closeSuccess]);

    return (
        <>
            <div className={styles.scroll}>
                <PageHeader
                    title="Запросы в техническую поддержку"
                    actions={<Button onClick={() => navigate('/support-answers')}>Мои обращения</Button>}
                />

                <div className={styles.flex}>
                    <SupportForm
                        values={values}
                        errors={errors}
                        onChange={setField}
                        files={files}
                        onAddFiles={handleAddFiles}
                        onRemoveFile={handleRemoveFile}
                        projectOptions={projectOptions}
                    />

                    <aside className={styles.aside}>
                        <ContactCard />
                        <TipsCard />
                        <MyTicketsCard
                            tickets={myTickets}
                            onOpen={(id) => navigate(`/support-answers/${id}`)}
                            onOpenAll={() => navigate('/support-answers')}
                        />
                    </aside>
                </div>
            </div>

            <SubmitBar
                authorName={authorName}
                org={orgName}
                isSending={isSending}
                onClear={resetAll}
                onSubmit={handleSubmit}
            />

            <SuccessModal ticket={submitted} onClose={closeSuccess} onCreateAnother={createAnother} />
        </>
    );
}